import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Heading, Flex, Select, Button, Text, HStack } from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import TestHistory from '../components/TestHistory';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useProjectContext } from '../context/ProjectContext';
import client from '../utils/fetchClient';
import HelpTooltip from '../components/ui/HelpTooltip';
import SearchInput from '../components/filters/SearchInput';
import FilterSelect from '../components/filters/FilterSelect';

function TestRunsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitFromUrl = useRef(false);
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam, loading: projectsLoading } = useProjectContext();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('weekly');
  const PAGE_SIZE = 20;
  const navigate = useNavigate();

  // Initialize from URL on mount
  useEffect(() => {
    if (didInitFromUrl.current) return;
    const qpPage = searchParams.get('page');
    const qpQ = searchParams.get('q');
    const qpPeriod = searchParams.get('period');
    if (qpPage) setCurrentPage(parseInt(qpPage, 10));
    if (qpQ) setSearchTerm(qpQ);
    if (qpPeriod) setPeriod(qpPeriod);
    didInitFromUrl.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync to URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (currentPage > 1) next.set('page', currentPage.toString());
    if (searchTerm) next.set('q', searchTerm);
    if (period) next.set('period', period);
    setSearchParams(next, { replace: true });
  }, [currentPage, searchTerm, period, setSearchParams]);

  // Fetch test runs when deps change
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (effectiveProjectParam) params.append('project', effectiveProjectParam);
    if (currentPage > 1) params.append('page', currentPage);
    if (PAGE_SIZE) params.append('page_size', PAGE_SIZE);
    if (period) params.append('period', period);
    client.get('/api/test-runs', { query: Object.fromEntries(params) })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.runs)
          ? data.runs
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];
        // Update pagination info if available
        if (data?.total !== undefined) {
          setTotalPages(Math.ceil(data.total / PAGE_SIZE));
        } else if (data?.count !== undefined) {
          setTotalPages(Math.ceil(data.count / PAGE_SIZE));
        } else {
          const calculatedPages = list.length === PAGE_SIZE ? currentPage + 1 : currentPage;
          setTotalPages(prev => Math.max(calculatedPages, prev));
        }
        setRuns(list);
        setLoading(false);
      })
      .catch(() => {
        setRuns([]);
        setLoading(false);
        setError("An error occurred while fetching test runs.");
      });
  }, [effectiveProjectParam, currentPage, period, PAGE_SIZE]);

  // Handler for comparing two runs
  const handleCompareRuns = (run1, run2) => {
    if (!run1 || !run2) return;
    navigate(`/compare?runA=${encodeURIComponent(run1.id)}&runB=${encodeURIComponent(run2.id)}`);
  };

  // Apply search client-side by run name/suite/file
  const filteredRuns = useMemo(() => {
    if (!searchTerm) return runs;
    const needle = searchTerm.toLowerCase();
    return (Array.isArray(runs) ? runs : []).filter(r => {
      const name = (r.name || r.file || r.suite || '').toLowerCase();
      return name.includes(needle);
    });
  }, [runs, searchTerm]);

  if (loading || projectsLoading) {
    return <LoadingState message="Loading test runs" minH="80vh" />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  }

  if (!filteredRuns || filteredRuns.length === 0) {
    return <EmptyState emoji="📜" title="No test runs found" subtitle="Try adjusting your filters or run some tests." />;
  }

  return (
    <Page>
      <Heading mb={6} fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="gray.800" display="flex" alignItems="center">Test Runs <HelpTooltip label="Browse and filter test runs. Click a row to view run details and failures." href="https://docs.example.com/qa/test-runs" /></Heading>
      
      {/* Controls */}
      <HStack spacing={3} wrap="wrap" mb={6} align="center" justify="space-between">
        <SearchInput
          placeholder="Search by group name."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
        />
        <FilterSelect
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
          ]}
          maxW="160px"
        />
      </HStack>
      
      {/* Project Filter (mobile only, desktop handled by header) */}
      <Flex mb={6} display={{ base: 'block', md: 'none' }}>
        <Select
          value={selectedProject || 'ALL PROJECTS'}
          onChange={e => { setSelectedProject(e.target.value); }}
          maxW="220px"
          bg="white"
          variant="filled"
          size="md"
          borderRadius="full"
        >
          <option value="ALL PROJECTS">All Projects</option>
          {projects.map((p, idx) => (
            <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
          ))}
        </Select>
      </Flex>
      
      <SectionCard>
        <TestHistory
        selectedProject={selectedProject}
        projects={projects}
        runs={filteredRuns}
        loading={loading}
        onCompareRuns={handleCompareRuns}
        />
      </SectionCard>
      
      {/* Pagination */}
      <Flex mt={8} spacing={4} justify="center" align="center">
        <Button
          size="sm"
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          isDisabled={currentPage === 1}
          colorScheme="green"
          variant="outline"
        >
          Previous
        </Button>
        <Text fontSize="sm" mx={4}>Page {currentPage} of {totalPages}</Text>
        <Button
          size="sm"
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          isDisabled={currentPage === totalPages}
          colorScheme="green"
          variant="outline"
        >
          Next
        </Button>
      </Flex>
    </Page>
  );
}

export default TestRunsPage; 